import { useState, useContext, useCallback } from 'react';
import { fetchItemInspect, withAuthCheck, AuthError } from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';

// Simple in-memory cache: key – itemCode, value – item data
export const itemCache = new Map();

/**
 * Hook for preloading item details before showing dialog
 * Returns loading state and a function to trigger loading
 */
export const useItemDetailsPreload = () => {
  const token = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [loadedItem, setLoadedItem] = useState(null);

  const preloadItemDetails = useCallback(async (itemData) => {
    if (!itemData || !itemData.code) {
      return false;
    }

    // If data is already in cache, we can show dialog immediately
    if (itemCache.has(itemData.code)) {
      setLoadedItem(itemData);
      // Small delay to ensure React state updates
      await new Promise(resolve => setTimeout(resolve, 0));
      return true;
    }

    // Start loading
    setLoading(true);
    try {
      const response = await withAuthCheck(fetchItemInspect, token, itemData.code);
      const fetchedItemData = response.item;
      
      // Save to cache
      itemCache.set(itemData.code, fetchedItemData);
      
      // Set loaded item to show dialog
      setLoadedItem(itemData);
      setLoading(false);
      return true;
    } catch (err) {
      setLoading(false);
      if (err instanceof AuthError) {
        // WebAuthForm will be shown automatically via AuthProvider
        return false;
      }
      // Still show dialog even if loading failed - ItemView will handle error state
      setLoadedItem(itemData);
      return true;
    }
  }, [token]);

  const clearLoadedItem = useCallback(() => {
    setLoadedItem(null);
  }, []);

  return {
    loading,
    loadedItem,
    preloadItemDetails,
    clearLoadedItem
  };
};